import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { PencilSquareIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { Formik, Form, Field } from 'formik';
import Modal from "src/components/Modal";
import { IconSpinner, IconX } from "src/components/icons";
import { Link, NavLink, Outlet, useNavigate, useParams } from "src/navigation";
import { entity_sizes, owner_types, PROwner, useIpOwners } from "./patent_renewals";
import { pureCountries } from "src/data";
import { settingsCardGrid, settingsCardLinkStyle } from "./settings";
import { CostCenterList } from "./CostCentersManagement";
import { useRoles } from "src/user/Auth";


export function OwnersManagement() {
    const { t } = useTranslation();
    const { canPrEditOwners } = useRoles()
    const { owners } = useIpOwners();
    return (
        <>
            {/* @ts-ignore */}
            <Helmet>
                <title>{t('owners')} | {t('renewal-fees')} | Patent Cockpit</title>
            </Helmet>
            <div className="main-content bg-pcx-100">
                <div className="flex flex-row gap-4 items-start">
                    <div className={settingsCardGrid}>
                        {owners?.map(owner => (
                            <NavLink
                                key={owner.id ?? 'new'}
                                to={`edit/${owner.id}`}
                                className={settingsCardLinkStyle}
                            >
                                {canPrEditOwners && <PencilSquareIcon className="h-6 w-6" />}{owner.name}
                            </NavLink>
                        ))}
                        {canPrEditOwners && 
                        <Link
                            to='add'
                            className={settingsCardLinkStyle({ isActive: false })}
                        >
                            <PlusCircleIcon className="h-6 w-6" />{t('add')}
                        </Link>}
                    </div>
                    <Outlet />
                </div>
            </div>
        </>
    );
}

const initialOwner = { name: '', country: 'CH', entity_size: null, type: null, industry: null, company_website: null };

function OwnerEditForm({owner, isAdding}: {owner?: PROwner, isAdding: boolean}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { postOwner } = useIpOwners();

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const initialValues = isAdding
        ? initialOwner
        : owner || initialOwner;

    return (
        <div className="rounded-lg bg-white shadow w-full sm:w-fit">
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={async (values, { resetForm }) => {
                    const result = await postOwner(values);
                    if (isAdding) {
                        navigate(`../edit/${result.id}`);
                    } else {
                        resetForm({ values });
                    }
                }}
            >{({ dirty, isSubmitting }) => (
                <Form>
                    <div className="flex flex-row justify-between p-4">
                        <h3>{isAdding ? t('add-owner-details') : t('edit-owner-details')}</h3>
                        <Link to=".."><IconX /></Link>
                    </div>
                    <div className="grid gap-4 pt-0 p-4">
                        <label>
                            <div className="label mb-1">{t('company-name')}</div>
                            <Field name="name" className="form-input w-full" required autoFocus />
                        </label>
                        <label>
                            <div className="label mb-1">{t('country')}</div>
                            <Field name="country" className="form-select w-full" required as="select" >
                                {pureCountries.map(c => <option key={c} value={c}>{c}</option>)}
                            </Field>
                        </label>
                        <label>
                            <div className="label mb-1">{t('entitySize')}</div>
                            <Field name="entity_size" className="form-select w-full" required as="select" >
                                <option value={''}>{t('select')}</option>
                                {entity_sizes.map(c => <option key={c} value={c}>{c}</option>)}
                            </Field>
                        </label>
                        <label>
                            <div className="label mb-1">{t('entityType')}</div>
                            <Field name="type" className="form-select w-full" required as="select" >
                                <option value={''}>{t('select')}</option>
                                {owner_types.map(c => <option key={c} value={c}>{c}</option>)}
                            </Field>
                        </label>
                    </div>
                    <div className="p-4 flex flex-row-reverse gap-4">
                        <button type="submit" disabled={!dirty} className="btn-primary disabled:btn-disabled">
                            {isAdding ? t('add') : t('save')} {isSubmitting && <IconSpinner className="ml-1 size-4 animate-spin inline" />}
                        </button>
                        <Link to=".." className="btn-secondary">{t('cancel')}</Link>
                        {!isAdding && <button type="button" className="btn-tertiary mr-auto" onClick={() => setShowDeleteModal(true)}>{t('delete')}</button>}
                    </div>
                </Form>
            )}</Formik>
            {showDeleteModal && (
                <OwnerDelete
                    name={initialValues.name}
                    cancelAction={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        // You may want to add deleteOwner logic here
                        setShowDeleteModal(false);
                        navigate('..');
                    }}
                />
            )}
        </div>
    )
}

function OwnerView({owner}: {owner?: PROwner}) {
    const {t} = useTranslation()

    if (!owner) return null

    const labels = [
        { field: 'name',        label: t('name')},
        { field: 'country',     label: t('country')},
        { field: 'entity_size', label: t('entitySize')},
        { field: 'type',        label: t('entityType')},
    ]
    return (
        <div className="rounded-lg bg-white shadow w-full sm:w-fit space-y-4 p-4">
            {labels.map(({field, label}, index) =>
                <div key={index}>
                    <div className="text-sm text-slate-400">{label}</div>
                    <div>{owner[field] ?? '-'}</div>
                </div>
            )}
        </div>
    )
}

export function OwnerEdit({ isAdding = false }: { isAdding?: boolean }) {
    const { canPrEditOwners } = useRoles()

    const { owners } = useIpOwners();
    const { id } = useParams();
    const owner = isAdding ? undefined : owners?.find(o => o.id === id);

    return <>
        {canPrEditOwners ? <OwnerEditForm {...{owner, isAdding}} /> : <OwnerView {...{owner}} />}
        {!isAdding && <CostCenterList owner_id={id} />}
    </>
}

function OwnerDelete({ name, cancelAction = () => { }, deleteAction = () => { } }: { name: string, cancelAction?: () => void, deleteAction?: () => void }) {
    const { t } = useTranslation();
    return (
        <Modal escAction={cancelAction} blurClick={cancelAction}>
            <div className="p-4 font-semibold">
                {t('delete-owner', { name })}
            </div>
            <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-4">
                <button type="button" className="btn-warn" onClick={deleteAction}>{t('delete')}</button>
                <button type="button" className="btn-secondary" onClick={cancelAction}>{t('cancel')}</button>
            </div>
        </Modal>
    );
}
